import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import api from "../lib/api";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Loader2, Search, X, Building2 } from "lucide-react";

const MAX = 25;

export const ChamberPicker = ({ onResults }) => {
  const [q, setQ] = useState("");
  const [sel, setSel] = useState([]);
  const [loading, setLoading] = useState(false);
  const { data: cats = [], isLoading, isError } = useQuery({
    queryKey: ["chamber-categories"],
    queryFn: () => api.get("/leads/chamber/categories").then((r) => r.data.categories),
    staleTime: 3600000,
  });
  const names = Object.fromEntries(cats.map((c) => [c.slug, c.name]));
  const shown = cats.filter((c) => c.name.toLowerCase().includes(q.trim().toLowerCase()));
  const toggle = (slug) =>
    setSel((s) => (s.includes(slug) ? s.filter((x) => x !== slug) : s.length < MAX ? [...s, slug] : s));

  const find = async () => {
    setLoading(true);
    try {
      const r = await api.post("/leads/chamber/extract", { slugs: sel });
      onResults(r.data);
      if (!r.data.count) toast.message("No members listed in those categories yet");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not read the Chamber directory");
    } finally {
      setLoading(false);
    }
  };

  if (isError) return <p className="text-sm text-destructive" data-testid="chamber-error">Chamber directory is unavailable right now.</p>;

  return (
    <div className="space-y-2" data-testid="chamber-picker">
      <div className="relative">
        <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={`Search ${cats.length || ""} categories…`}
          className="pl-9" data-testid="chamber-category-search" />
      </div>
      {sel.length > 0 && (
        <div className="flex flex-wrap gap-1.5" data-testid="chamber-selected">
          {sel.map((s) => (
            <button key={s} onClick={() => toggle(s)} data-testid={`chamber-chip-${s}`}
              className="inline-flex items-center gap-1 rounded-full bg-primary/15 text-primary text-xs px-2.5 py-1 hover:bg-primary/25 transition-colors">
              {names[s]} <X className="h-3 w-3" />
            </button>
          ))}
        </div>
      )}
      <div className="max-h-48 overflow-y-auto rounded-lg border border-border divide-y divide-border" data-testid="chamber-category-list">
        {isLoading && <p className="p-3 text-xs text-muted-foreground flex items-center gap-2"><Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading categories…</p>}
        {shown.map((c) => (
          <label key={c.slug} className="flex items-center gap-2.5 px-3 py-2 text-sm cursor-pointer hover:bg-secondary/40">
            <input type="checkbox" checked={sel.includes(c.slug)} onChange={() => toggle(c.slug)}
              disabled={!sel.includes(c.slug) && sel.length >= MAX} className="accent-cyan-400"
              data-testid={`chamber-cat-${c.slug}`} />
            <span className="truncate">{c.name}</span>
          </label>
        ))}
        {!isLoading && !shown.length && <p className="p-3 text-xs text-muted-foreground">No categories match “{q}”.</p>}
      </div>
      <Button onClick={find} disabled={loading || !sel.length} className="w-full" data-testid="chamber-find-btn">
        {loading ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Building2 className="h-4 w-4 mr-1.5" />}
        Find members in {sel.length || "selected"} categor{sel.length === 1 ? "y" : "ies"}
      </Button>
    </div>
  );
};
